/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormGroup,
  FormBuilder,
  Validators,
  UntypedFormControl,
} from '@angular/forms';
import {
  ReportsSetupFormService,
  ReportFormData,
  ReportKind,
  ResolvedQuery,
} from '../reports-setup-form-service';
import { PortfolioApiService } from 'src/app/shared/services/customer-api/portfolio-api.service';
import { UserInfoService } from 'src/app/shared/services/user-info.service';
import { Observable, combineLatest, firstValueFrom } from 'rxjs';
import { map, debounceTime, tap } from 'rxjs/operators';
import { SearchService } from 'src/app/shared/services/metadata-api/search.service';
import { ENTER } from '@angular/cdk/keycodes';
import { MatLegacyChipInputEvent as MatChipInputEvent } from '@angular/material/legacy-chips';
import { ReportsService } from '../reports-service';
import {
  CockpitScoreOption,
  ENTITY_DISALLOWED_REPORT_TYPES,
  ENTITY_EXTRA_REPORT_TYPES,
  FREQUENCY_OPTIONS,
  PORTFOLIO_EXTRA_REPORT_TYPES,
  PORTFOLIO_TOO_FEW_COMPANIES_TOOLTIP,
  QUERY_REPORT_TYPES,
  REPORT_SUPPORTED_SCORES,
  ReportType,
  ReportTypeOption,
  Schedule,
  hasEnoughCompaniesForReport,
  scoreToReportType,
} from 'src/app/shared/utils/reports';
import { SettingsUtilsService } from '../../settings-utils.service';
import { QueryApiService } from 'src/app/shared/services/customer-api/query-api.service';
import { REPORT_KIND_CONFIG } from 'src/app/micro-components/dialog-report/dialog-report.component';
import getScoreTypeDetails from 'src/app/shared/utils/score-type-details';

const DUE_DILIGENCE_REPORT_TYPES: ReadonlySet<ReportType> = new Set<ReportType>(
  [
    'due_diligence_tabular',
    'due_diligence_tabular_portfolio',
    'esgr_due_diligence_tabular',
    'esgr_due_diligence_tabular_portfolio',
  ],
);

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-reports-form',
  templateUrl: './reports-form.component.html',
  styleUrls: ['./reports-form.component.scss'],
})
export class ReportsFormComponent implements OnInit {
  @Input() scoreOptions: CockpitScoreOption[] = [];
  @Output() titleChange = new EventEmitter<string>(true);
  @Output() bulletsChange = new EventEmitter<string[]>(true);

  private readonly destroyRef = inject(DestroyRef);

  reportForm: FormGroup;
  currentKind: ReportKind = 'entity';
  isNewReport = true;
  searchFormControl = new UntypedFormControl();
  folio$: Observable<any>;
  queryGroups$: Observable<{ uid: string; name: string; queries: any[] }[]>;
  searchItems$: Observable<any>;
  loadingSearch = false;
  reportTypes: ReportTypeOption[] = [];

  private folioSnapshot: { uid: string; name: string }[] = [];
  private querySnapshot: {
    uid: string;
    name: string;
    queries: { uid: string; name: string }[];
  }[] = [];
  private currentEntityName = '';
  private currentPortfolioName = '';
  private currentQueryName = '';

  get availableReportTypes(): ReportTypeOption[] {
    switch (this.currentKind) {
      case 'query':
        return [...QUERY_REPORT_TYPES];
      case 'portfolio':
        return [...PORTFOLIO_EXTRA_REPORT_TYPES, ...this.reportTypes];
      case 'entity':
      default:
        return [
          ...ENTITY_EXTRA_REPORT_TYPES,
          ...this.reportTypes.filter(
            (rt) => !ENTITY_DISALLOWED_REPORT_TYPES.has(rt.value),
          ),
        ];
    }
  }

  reportTypeGroups: { label: string; options: ReportTypeOption[] }[] = [];

  trackByGroupLabel = (_: number, g: { label: string }) => g.label;
  trackByOptionValue = (_: number, o: ReportTypeOption) => o.value;

  private updateReportTypeGroups(): void {
    const all = this.availableReportTypes;
    const dd = all.filter((o) => DUE_DILIGENCE_REPORT_TYPES.has(o.value));
    const score = all.filter((o) => !DUE_DILIGENCE_REPORT_TYPES.has(o.value));
    const groups: { label: string; options: ReportTypeOption[] }[] = [];
    if (dd.length) groups.push({ label: 'Due Diligence', options: dd });
    if (score.length) groups.push({ label: 'Score reports', options: score });
    this.reportTypeGroups = groups;
  }

  readonly scheduleTabs = FREQUENCY_OPTIONS;
  emailList: string[] = [];
  readonly separatorKeysCodes: number[] = [ENTER];
  readonly portfolioTooFewCompaniesTooltip =
    PORTFOLIO_TOO_FEW_COMPANIES_TOOLTIP;

  get currentScheduleHint(): string {
    const value = this.reportForm?.get('schedule')?.value as Schedule | null;
    return FREQUENCY_OPTIONS.find((opt) => opt.value === value)?.hint ?? '';
  }

  canReportPortfolio(
    folio: { content?: string[] } | null | undefined,
  ): boolean {
    return hasEnoughCompaniesForReport(folio?.content);
  }

  /** User's email domain (e.g. `yukkalab.com`) — used to validate recipient additions and as the chip-list label. */
  get domain(): string {
    return this.userInfo.email?.split('@')[1] ?? '';
  }

  constructor(
    private fb: FormBuilder,
    private reportFormService: ReportsSetupFormService,
    private reportService: ReportsService,
    private userInfo: UserInfoService,
    private portfolioApi: PortfolioApiService,
    private searchService: SearchService,
    private settingsUtils: SettingsUtilsService,
    private queryApi: QueryApiService,
  ) {
    this.folio$ = this.portfolioApi.portFolios();
    this.queryGroups$ = combineLatest([
      this.queryApi.getQuery(),
      this.queryApi.getGroupsQuery(),
    ]).pipe(
      map(([queries, collections]: [any[], any[]]) => {
        const grouped = (collections || []).map((c) => ({
          uid: c.uid,
          name: c.name,
          queries: (queries || []).filter((q) =>
            (q.groups || []).includes(c.uid),
          ),
        }));
        const uncategorized = (queries || []).filter(
          (q) => !q.groups || q.groups.length === 0,
        );
        if (uncategorized.length > 0) {
          grouped.push({
            uid: '__uncategorized',
            name: 'Uncategorized',
            queries: uncategorized,
          });
        }
        return grouped.filter((g) => g.queries.length > 0);
      }),
    );
  }

  ngOnInit(): void {
    this.initForm();
    this.initializeReportTypes();

    this.reportForm
      .get('kind')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((kind: ReportKind) => {
        this.currentKind = kind || 'entity';
        this.updateReportTypeGroups();
      });

    this.searchFormControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: string) => {
        this.currentEntityName = value ?? '';
        this.recomputeHeader();
      });

    this.searchFormControl.valueChanges
      .pipe(debounceTime(1000), takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => this.runEntitySearch(value));

    this.reportForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.recomputeHeader());

    this.folio$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((folios) => {
        this.folioSnapshot = (folios ?? []) as { uid: string; name: string }[];
        this.recomputeHeader();
      });

    this.queryGroups$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((groups) => {
        this.querySnapshot = groups ?? [];
        this.recomputeHeader();
      });

    this.reportFormService.currentReport$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((report) => {
        this.updateForm(report);
        this.isNewReport = report.isNew;
      });
  }

  private recomputeHeader(): void {
    if (!this.reportForm) return;
    const kind = this.reportForm.get('kind')?.value as ReportKind | null;
    const reportObject = this.reportForm.get('reportObject')?.value as
      | string
      | null;
    const reportType = this.reportForm.get('reportType')?.value as
      | ReportType
      | '';

    const objectName = this.resolveObjectName(kind, reportObject);
    const reportTypeLabel = this.availableReportTypes.find(
      (rt) => rt.value === reportType,
    )?.label;

    const title =
      kind && reportObject && reportType && objectName && reportTypeLabel
        ? `${objectName} · ${reportTypeLabel}`
        : 'New subscription';
    this.titleChange.emit(title);

    const bullets = DUE_DILIGENCE_REPORT_TYPES.has(reportType as ReportType)
      ? REPORT_KIND_CONFIG.due_diligence.bullets
      : REPORT_KIND_CONFIG.score.bullets;
    this.bulletsChange.emit(bullets);
  }

  private resolveObjectName(
    kind: ReportKind | null,
    reportObject: string | null,
  ): string {
    if (!kind || !reportObject) return '';
    switch (kind) {
      case 'entity':
        return this.currentEntityName;
      case 'portfolio':
        return (
          this.folioSnapshot.find((f) => f.uid === reportObject)?.name ||
          this.currentPortfolioName
        );
      case 'query':
        return (
          this.querySnapshot
            .flatMap((g) => g.queries)
            .find((q) => q.uid === reportObject)?.name || this.currentQueryName
        );
    }
  }

  initForm(): void {
    if (this.reportForm) return;
    this.reportForm = this.fb.group({
      id: [null],
      kind: ['entity', Validators.required],
      active: [true],
      reportObject: [null, Validators.required],
      schedule: [null, Validators.required],
      reportType: ['', Validators.required],
      recipients: [[]],
      isNew: [true],
    });
  }

  updateForm(report: ReportFormData): void {
    this.initForm();
    if (report.kind === 'entity') {
      this.searchFormControl.setValue(report.entityName || '');
    }

    this.currentEntityName =
      report.entityName ?? (this.searchFormControl.value || '');
    this.currentPortfolioName = report.portfolioName ?? '';
    this.currentQueryName = report.queryName ?? '';

    // Existing subscriptions may still have legacy uppercase schedule values
    // (DAILY/WEEKLY/...) — normalize so the dropdown can match them.
    const normalizedSchedule = report.schedule
      ? (report.schedule.toString().toLowerCase() as Schedule)
      : report.schedule;

    this.reportForm.patchValue({
      id: report.id,
      kind: report.kind,
      active: report.active,
      reportObject: report.reportObject,
      schedule: normalizedSchedule,
      reportType: report.reportType,
      recipients: report.recipients,
      isNew: report.isNew,
    });

    this.emailList = report.recipients || [];
    this.reportForm.get('recipients')?.setValue(this.emailList);
    this.recomputeHeader();
  }

  private runEntitySearch(value: string): void {
    if (!value || value.length <= 1) return;
    this.loadingSearch = true;
    this.searchItems$ = this.searchService
      .searchWithFallback(value, { companiesOnly: true })
      .pipe(tap(() => (this.loadingSearch = false)));
  }

  async saveReport(): Promise<void> {
    if (!this.reportForm.valid) {
      this.markFormGroupTouched(this.reportForm);
      return;
    }

    const formData: ReportFormData = {
      ...this.reportForm.value,
      recipients: this.emailList,
      active: true,
      // Not a form control — carried over so an edit keeps the language the
      // subscription was created with.
      language: this.reportFormService.getCurrentReport().language,
    };

    let portfolioName: string | undefined;
    let companyIds: string[] | undefined;
    if (formData.kind === 'portfolio') {
      const folios = await firstValueFrom(this.folio$);
      const selected = (folios || []).find(
        (p: any) => p.uid === formData.reportObject,
      );
      if (!hasEnoughCompaniesForReport(selected?.content)) {
        alert(PORTFOLIO_TOO_FEW_COMPANIES_TOOLTIP);
        return;
      }
      portfolioName = selected?.name;
      companyIds = (selected?.content as string[]) ?? [];
    }

    let resolvedQuery: ResolvedQuery | undefined;
    if (formData.kind === 'query') {
      resolvedQuery = await this.resolveSelectedQuery(formData.reportObject);
      if (!resolvedQuery) return;
    }

    const subscription = this.reportFormService.convertFormToSubscription(
      formData,
      portfolioName,
      companyIds,
      resolvedQuery,
    );

    this.reportService.createOrUpdateSubscription(subscription).subscribe({
      next: () => {
        this.reportFormService.resetForm();
      },
    });
  }

  private async resolveSelectedQuery(
    uid: string | undefined,
  ): Promise<ResolvedQuery | undefined> {
    if (!uid) return undefined;
    try {
      const groups = await firstValueFrom(this.queryGroups$);
      const picked = (groups || [])
        .flatMap((g) => g.queries)
        .find((q) => q.uid === uid);
      if (!picked) return undefined;
      const searchQuery = await firstValueFrom(this.queryApi.resolveQuery(uid));
      return { name: picked.name, searchQuery: searchQuery ?? {} };
    } catch (err) {
      console.error('resolveSelectedQuery failed:', err);
      return undefined;
    }
  }

  onOptionSelected(option: any): void {
    this.searchFormControl.setValue(option.entity.name);
    this.reportForm.patchValue({
      reportObject:
        option.entity.compound_key ||
        `${option.entitiy.type}:${option.entity.id} `,
      kind: 'entity',
    });
  }

  deleteReport(): void {
    const id = this.reportForm.get('id')?.value;
    this.reportService.deleteSubscription(id).subscribe({
      next: () => {
        this.reportFormService.resetForm();
      },
      error: (error) => {
        console.error('Error deleting subscription', error);
        alert('Error deleting subscription. Please try again.');
      },
    });
  }

  /**
   * format big numbers >999 into K, M, ...
   */
  numberFormat(num: number) {
    return this.settingsUtils.numberFormat(num);
  }

  iMatch(entity) {
    return this.settingsUtils.iMatch(entity);
  }

  removeEmail(email: string): void {
    this.emailList = this.settingsUtils.removeEmail(this.emailList, email);
    this.reportForm.get('recipients')?.setValue(this.emailList);
  }

  addEmail(event: MatChipInputEvent): void {
    const { updatedList, error } = this.settingsUtils.handleEmailAdd(
      event.value,
      this.emailList,
      this.userInfo.email ?? '',
      this.domain,
    );
    if (error) {
      alert(error);
    } else {
      this.emailList = updatedList;
      this.reportForm.get('recipients')?.setValue(this.emailList);
    }
    if (event.input) {
      event.input.value = '';
    }
  }

  onKindChange() {
    const selectedKind: ReportKind =
      this.reportForm.get('kind')?.value ?? 'entity';
    this.currentKind = selectedKind;
    this.searchFormControl.setValue('');

    const currentReportType = this.reportForm.get('reportType')?.value;
    const validValues = this.availableReportTypes.map((s) => s.value);
    if (!validValues.includes(currentReportType)) {
      this.reportForm.patchValue({ reportType: validValues[0] ?? '' });
    }
    this.updateReportTypeGroups();
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  private initializeReportTypes() {
    this.reportTypes = this.scoreOptions
      .filter((item) => REPORT_SUPPORTED_SCORES.has(item.value))
      .map((item) => {
        const wireValue = scoreToReportType(item.value);
        if (!wireValue) return null;
        const reportLabel = getScoreTypeDetails(item.value).reportLabel;
        return { value: wireValue, label: reportLabel || item.label };
      })
      .filter((x): x is ReportTypeOption => x !== null);
    this.updateReportTypeGroups();
  }
}
